import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ArrowUpRight, CheckCircle2, Lock } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw.length < 6) {
      setErr("Password must be at least 6 characters.");
      return;
    }
    if (pw !== confirm) {
      setErr("Passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      setTimeout(() => nav("/login", { replace: true }), 2500);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-[88vh] flex items-center justify-center px-6 py-20">
        <div className="max-w-md w-full text-center" data-testid="reset-missing-token">
          <div className="tag mb-4 mx-auto">// no token</div>
          <h1 className="font-heading font-black text-4xl tracking-tighter mb-4">
            Missing reset token
          </h1>
          <p className="text-zinc-400 mb-8 text-sm">
            This page needs to be opened from the link in your reset email.
          </p>
          <Link to="/forgot-password" className="btn-primary inline-flex">
            Request a new link <ArrowUpRight size={14} />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[88vh] flex items-center justify-center px-6 py-20">
      <div className="w-full max-w-md">
        <div className="tag mb-4">// set new password</div>
        <h1 className="font-heading font-black text-4xl md:text-5xl tracking-tighter mb-3">
          New password
        </h1>
        <p className="text-zinc-400 mb-8 text-sm">
          Choose something strong. You&apos;ll be signed in afterwards.
        </p>

        {done ? (
          <div
            className="border border-[var(--primary)]/40 bg-[var(--primary)]/5 p-6 text-center"
            data-testid="reset-password-success"
          >
            <div className="w-12 h-12 mx-auto mb-4 border border-[var(--primary)]/60 bg-[var(--primary)]/10 flex items-center justify-center">
              <CheckCircle2 className="text-[var(--primary)]" size={20} strokeWidth={1.5} />
            </div>
            <h3 className="font-heading font-bold text-xl mb-1">Password updated</h3>
            <p className="text-zinc-300 text-sm">Redirecting to sign in…</p>
            <Link to="/login" className="inline-flex items-center gap-2 text-[var(--primary)] hover:text-white text-xs font-mono uppercase tracking-widest mt-4">
              Go to sign in <ArrowUpRight size={14} />
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5" data-testid="reset-password-form">
            <div>
              <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">
                New password
              </label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" strokeWidth={1.5} />
                <input
                  required
                  type="password"
                  data-testid="reset-password"
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  className="input-gold pl-10"
                  placeholder="At least 6 characters"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">
                Confirm new password
              </label>
              <input
                required
                type="password"
                data-testid="reset-password-confirm"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="input-gold"
              />
            </div>

            {err && (
              <div className="border border-red-500/40 bg-red-500/10 text-red-300 text-sm px-4 py-3" data-testid="reset-error">
                {err}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="reset-submit"
              className="btn-primary w-full justify-center"
            >
              {loading ? "Updating…" : "Set new password"} <ArrowUpRight size={16} />
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
